import React, { useMemo, useRef } from 'react';
import { Tag as TagGlyph } from 'shared/assets/icons';
import { ActionTaskContextMenu } from 'shared/components/connected/action-task-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { tooltipDelay } from 'shared/constants';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';

import * as Styled from './event.style';
import { EventCard } from './event-card';

export type TaskTagEventProps = {
  task: Task;
  tags: Tag[];
  onClick?: (id: ID) => void;
  numberOfLines: number;
  timeText?: string;
  isPast?: boolean;
  completed?: boolean;
  onInteractionComplete?: () => void;
};

// Acceptable parallel of TaskLifeAreaEvent/TaskSmartListEvent (reuse-over-reinvent.md):
// one calendar grid card per timeslot entity; they share the EventCard shape but
// differ on the leading icon and color source.
export const TaskTagEvent: React.FC<TaskTagEventProps> = ({
  task,
  tags,
  onClick,
  numberOfLines,
  timeText,
  isPast,
  completed,
  onInteractionComplete,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const containerRef = useRef<HTMLDivElement>(null);
  const _onClick = () => onClick?.(task.id);

  const tag = useMemo(
    () => tags.find(({ id }) => id === task.action?.meta.id),
    [tags, task.action?.meta.id],
  );

  if (!tag) {
    return null;
  }

  return (
    <>
      <EventCard
        ref={containerRef}
        title={tag.name}
        color={tag.color}
        timeText={timeText}
        completed={completed}
        isPast={isPast}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        leading={
          <Styled.LeadingSlot $color={tag.color} $past={isPast}>
            <Icon icon={TagGlyph} />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <ActionTaskContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          onAction={onInteractionComplete}
        />
      )}
      <Tooltip
        referenceElement={containerRef}
        label={tag.name}
        delay={tooltipDelay}
      />
    </>
  );
};
